import React, { useLayoutEffect, useRef, useState } from 'react';
/**
 * Visibility-aware wrapper for FullCalendar eventContent.
 *
 * FullCalendar renders all events in the DOM but hides overflow events by setting
 * `visibility: hidden` on their `.fc-daygrid-event-harness` wrapper. Each hidden
 * event still builds its full React tree (~10 DOM nodes), so with 930 hidden events
 * in a busy month that is ~9,300 wasted nodes.
 *
 * This component short-circuits the render for hidden events, replacing the full
 * tree with an empty `<span />`. When the "+N more" popover opens, a MutationObserver
 * detects the harness visibility change and re-renders the full tree inside the popover.
 *
 * Only used when the Calendar molecule is in optimization mode (dayMaxEvents set).
 */
export function CalendarEventContent({ fcProps, renderEvent }) {
    const ref = useRef(null);
    const [isVisible, setIsVisible] = useState(null);
    useLayoutEffect(() => {
        var _a;
        const harness = (_a = ref.current) === null || _a === void 0 ? void 0 : _a.closest('.fc-daygrid-event-harness');
        if (!harness) {
            // In non-dayGrid views (week, day) the harness doesn't exist — always render.
            setIsVisible(true);
            return undefined;
        }
        const syncVisibility = () => {
            setIsVisible(getComputedStyle(harness).visibility !== 'hidden');
        };
        syncVisibility();
        // Keep observer running in both directions so the component correctly
        // short-circuits again when the popover closes and the harness is re-hidden.
        const observer = new MutationObserver(syncVisibility);
        observer.observe(harness, { attributes: true, attributeFilter: ['style'] });
        return () => {
            observer.disconnect();
        };
    }, []);
    if (isVisible !== true) {
        return React.createElement("span", { ref: ref });
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    return React.createElement("span", { ref: ref }, renderEvent(fcProps));
}
